import React, { useMemo, useState } from 'react';
import { Database, Globe, RefreshCw, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, EmptyState, ErrorState, LoadingState, SeverityBadge, StatCard, useConfirm } from '@/components/primitives';
import { Button } from '@/components/ui/button';
import { adminApi, formatUtc, plural, toastError, useAdminQuery, useBusy } from './adminApi';
import { AdminSection, FilterChips, Pager, RefreshError, SearchField, Toolbar } from './AdminUi';

const PAGE = 50;
const SEVERITIES = [
  { value: null, label: 'All' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

/**
 * Breach monitoring across all tenants: what customers monitor, what was
 * found, and a manual re-check. Credentials themselves are never shown here.
 */
export default function BreachTab() {
  const domains = useAdminQuery(() => adminApi.get('/admin/monitored-domains').then((r) => r.data), []);
  const results = useAdminQuery(() => adminApi.get('/admin/breach-results').then((r) => r.data), []);
  const dataset = useAdminQuery(() => adminApi.get('/admin/local-breach-status').then((r) => r.data), []);
  const [severity, setSeverity] = useState(null);
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const { run, busy } = useBusy();
  const [confirm, confirmDialog] = useConfirm();

  const stats = results.data?.stats;
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (results.data?.results || []).filter((r) =>
      (!severity || r.severity === severity)
      && (!needle || `${r.email} ${r.breach_name} ${r.user_email}`.toLowerCase().includes(needle)));
  }, [results.data, severity, q]);

  const checkAll = async () => {
    const emails = (await adminApi.get('/admin/monitored-emails').then((r) => r.data.total).catch(() => null));
    const ok = await confirm({
      title: 'Re-check every monitored email?',
      description: `${emails != null ? plural(emails, 'address') : 'All addresses'} across all tenants will be sent to the breach lookup services. This can take a while and uses their rate limits.`,
      confirmLabel: 'Run the check',
    });
    if (!ok) return;
    run('check', async () => {
      try {
        const res = await adminApi.post('/admin/breach-check-all');
        toast.success('Breach check finished', { description: `${res.data.checked ?? 0} checked, ${res.data.breaches_found ?? 0} breaches found, ${res.data.errors ?? 0} errors.` });
        results.reload();
      } catch (err) {
        toastError(err, 'The breach check failed');
      }
    });
  };

  return (
    <AdminSection
      title="Breach monitoring"
      description="Everything customers have asked IntelliSecure to watch. Passwords are only revealed to the verified domain owner, never in this console."
      actions={(
        <Button size="sm" variant="outline" onClick={checkAll} disabled={busy('check')} data-testid="breach-check-all-btn">
          <RefreshCw className={busy('check') ? 'adm-spin' : undefined} aria-hidden="true" /> {busy('check') ? 'Checking…' : 'Re-check all'}
        </Button>
      )}
    >
      <div className="adm-toolbar" aria-label="Summary">
        <div style={{ flex: '1 1 140px' }}><StatCard label="Monitored domains" value={domains.data?.total} loading={domains.loading} error={!!domains.error && !domains.data} icon={Globe} /></div>
        <div style={{ flex: '1 1 140px' }}><StatCard label="Breach results" value={stats?.total} loading={results.loading} error={!!results.error && !results.data} icon={ShieldAlert} /></div>
        <div style={{ flex: '1 1 140px' }}><StatCard label="Critical" value={stats?.critical} tone="critical" loading={results.loading} error={!!results.error && !results.data} /></div>
        <div style={{ flex: '1 1 140px' }}><StatCard label="Passwords exposed" value={stats?.passwords_exposed} tone="high" loading={results.loading} error={!!results.error && !results.data} /></div>
        <div style={{ flex: '1 1 140px' }}>
          <StatCard label="Local dataset records" value={dataset.data?.total_records?.toLocaleString()} loading={dataset.loading} error={!!dataset.error && !dataset.data} icon={Database} hint={dataset.data ? plural(dataset.data.files?.length || 0, 'file') : undefined} />
        </div>
      </div>

      <h3 className="adm-subhead-title">Monitored domains</h3>
      {domains.loading ? <LoadingState label="domains" rows={3} />
        : domains.error && !domains.data ? <ErrorState title="Couldn't load domains" error={domains.error} onRetry={domains.reload} />
          : !domains.data.domains.length ? <EmptyState variant="compact" icon={Globe} title="No domains monitored" description="Customers add domains in Breach Monitor." />
            : (
              <div className="adm-table-wrap">
                <table className="adm-table adm-table--stack" data-testid="admin-domains-table">
                  <thead>
                    <tr><th scope="col">Domain</th><th scope="col">Tenant</th><th scope="col">Ownership</th><th scope="col" className="adm-num">Emails</th><th scope="col" className="adm-num">Breaches</th></tr>
                  </thead>
                  <tbody>
                    {domains.data.domains.map((d) => (
                      <tr key={d.id}>
                        <td data-label="Domain" className="adm-mono adm-break">{d.domain}</td>
                        <td data-label="Tenant" className="adm-mono adm-break">{d.user_email}</td>
                        <td data-label="Ownership">{d.verified ? <Badge tone="success" size="sm" plain>Verified</Badge> : <Badge tone="warning" size="sm" plain>Unverified</Badge>}</td>
                        <td data-label="Emails" className="adm-num">{d.email_count}</td>
                        <td data-label="Breaches" className="adm-num">{d.breach_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

      <h3 className="adm-subhead-title">Breach results</h3>
      <Toolbar>
        <SearchField value={q} onChange={(v) => { setQ(v); setOffset(0); }} label="Search breach results" placeholder="Search email, breach or tenant" />
        <FilterChips label="Severity" options={SEVERITIES} value={severity} onChange={(v) => { setSeverity(v); setOffset(0); }} testIdPrefix="breach-sev" />
      </Toolbar>
      {results.loading ? <LoadingState label="breach results" rows={4} />
        : results.error && !results.data ? <ErrorState title="Couldn't load breach results" error={results.error} onRetry={results.reload} />
          : !results.data.results.length ? <EmptyState variant="compact" icon={ShieldAlert} title="No breach results" description="Nothing has been found for the monitored addresses." />
            : !rows.length ? <EmptyState variant="compact" title="No matching results" description="Try clearing the search or severity filter." />
              : (
                <>
                  <RefreshError error={results.error} onRetry={results.reload} what="breach results" />
                  <div className="adm-table-wrap">
                    <table className="adm-table adm-table--stack" data-testid="admin-breach-table">
                      <thead>
                        <tr><th scope="col">Email</th><th scope="col">Tenant</th><th scope="col">Breach</th><th scope="col">Severity</th><th scope="col">Password</th><th scope="col">Found</th></tr>
                      </thead>
                      <tbody>
                        {rows.slice(offset, offset + PAGE).map((r) => (
                          <tr key={r.id}>
                            <td data-label="Email" className="adm-mono adm-break">{r.email}</td>
                            <td data-label="Tenant" className="adm-mono adm-break adm-secondary">{r.user_email}</td>
                            <td data-label="Breach" className="adm-break">{r.breach_name || r.source}</td>
                            <td data-label="Severity"><SeverityBadge severity={r.severity} size="sm" short /></td>
                            <td data-label="Password">{r.has_password ? <Badge tone="danger" size="sm" plain>Exposed</Badge> : <span className="adm-muted">No</span>}</td>
                            <td data-label="Found" className="adm-mono adm-nowrap adm-secondary">{formatUtc(r.discovered_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pager offset={offset} limit={PAGE} total={rows.length} onChange={setOffset} label="Breach results pagination" />
                </>
              )}
      {confirmDialog}
    </AdminSection>
  );
}
